import React, { useState, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import { useDashboardData, getChartData } from '../../hooks/useDashboardData';
import { useResponsive } from '../../hooks/useResponsive';
import { useMonthlyHourlyTrendQuery } from '../../hooks/queries/useDashboardQuery';
import './HourlySalesChart.css';
import '../charts/ChartStyles.css';

interface HourlyData {
  hour: number;
  net_sales: string;
  order_count: string;
  avg_hourly_sales: string;
}

interface MonthlyHourlyData {
  year_month: string;
  hours: {
    [key: number]: {
      hour_of_day: number;
      total_net_sales: string;
      days_count: number;
      avg_daily_sales: string;
      total_order_count: number;
      avg_daily_orders: string;
    }
  }
}

interface ModalPosition {
  y: number;
}

const extractNumber = (value: string): number => {
  if (typeof value === 'string') {
    return parseFloat(value.replace(/[^0-9.-]+/g, ''));
  }
  return value || 0;
};

const formatHour = (hour: number): string => {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};

const formatMonthForDisplay = (yearMonth: string) => {
  const [year, month] = yearMonth.split('-');
  return `${year}/${month}`;
};

const calculateYAxisRange = (values: number[]) => {
  const validValues = values.filter(v => v > 0 && isFinite(v));
  
  if (validValues.length === 0) {
    return { min: 0, max: 100 };
  }
  
  const minValue = Math.min(...validValues);
  const maxValue = Math.max(...validValues);
  const range = maxValue - minValue;
  
  return {
    min: Math.max(0, Math.floor(minValue - range * 0.05)),
    max: Math.ceil(maxValue + range * 0.05)
  };
};

const HourlySalesChart: React.FC = () => {
  const { t } = useTranslation();
  const { data: dashboardData, loading, error, salesforceId } = useDashboardData();
  const { 
    isFiltersApplying,
    dayOfWeek,
    diningBehavior 
  } = useFilters();
  const isPhoneScreen = window.innerWidth <= 480;

  const [showMonthlyTrend, setShowMonthlyTrend] = useState<boolean>(false);
  const [modalPosition, setModalPosition] = useState<ModalPosition>({ y: 0 });
  
  
  const { 
    data: monthlyTrendData, 
    isLoading: trendLoading, 
    error: trendQueryError
  } = useMonthlyHourlyTrendQuery(salesforceId || '', showMonthlyTrend && !!salesforceId);
  
  const trendError = trendQueryError ? t('hourlyChart.monthlyTrendError') : null;
  
  const trendChartRef = useRef<any>(null);
  
  const [allLegendsSelected, setAllLegendsSelected] = useState<boolean>(true);
  
  const colorPalette = {
    primaryColor: '#F97F76',
    highlightColor: '#F52314',
    lowlightColor: '#8A9297',
    textColor: '#333333'
  };

  const hourColors: { [key: number]: string } = {
    0: '#E74C3C',   1: '#9B59B6',   2: '#3498DB',   3: '#1ABC9C',
    4: '#2ECC71',   5: '#F1C40F',   6: '#E67E22',   7: '#F97F76',
    8: '#8E44AD',   9: '#2980B9',   10: '#16A085',  11: '#27AE60',
    12: '#474646',  13: '#D35400',  14: '#C0392B',  15: '#7F8C8D',
    16: '#34495E',  17: '#F39C12',  18: '#E91E63',  19: '#9C27B0',
    20: '#673AB7',  21: '#3F51B5',  22: '#00BCD4',  23: '#4CAF50'
  };

  const { isLaptop, isiPad: isIPad, isiPhone: isIPhone } = useResponsive();

  const calculateModalPosition = (event: React.MouseEvent): ModalPosition => {
    const clickY = event.clientY;
    const modalHeight = 700;
    const padding = 20;
    const screenHeight = window.innerHeight;
    
    let finalY = clickY;
    
    if (clickY + modalHeight + padding > screenHeight) {
      finalY = screenHeight - modalHeight - padding;
    }
    
    if (finalY < padding) {
      finalY = padding;
    }
    
    return { y: finalY };
  };


  const handleMonthlyTrendClick = (event: React.MouseEvent) => {
    const position = calculateModalPosition(event);
    setModalPosition(position);
    setShowMonthlyTrend(true);
  };

  const handleToggleAllLegends = () => {
    if (trendChartRef.current && monthlyTrendData && Array.isArray(monthlyTrendData)) {
      const chartInstance = trendChartRef.current.getEchartsInstance();
      
      
      const allHours = new Set<number>();
      monthlyTrendData.forEach((monthData: MonthlyHourlyData) => {
        if (monthData.hours) {
          Object.keys(monthData.hours).forEach(hour => {
            allHours.add(parseInt(hour));
          });
        }
      });
      
      const hourLabels = Array.from(allHours).sort((a, b) => a - b).map(hour => formatHour(hour));

      if (allLegendsSelected) {
        hourLabels.forEach(hourLabel => {
          chartInstance.dispatchAction({
            type: 'legendUnSelect',
            name: hourLabel
          });
        });
        setAllLegendsSelected(false);
      } else {
        hourLabels.forEach(hourLabel => {
          chartInstance.dispatchAction({
            type: 'legendSelect',
            name: hourLabel
          });
        });
        setAllLegendsSelected(true);
      }
    }
  };

  const getFilteredHourlyData = (): HourlyData[] => {
    if (!dashboardData) return [];

    const filters = {
      dayOfWeek: (!dayOfWeek.allDays && dayOfWeek.selectedDays.length > 0) ? dayOfWeek.selectedDays : undefined,
      hour: undefined,
      behavior: (!diningBehavior.allBehaviors && diningBehavior.selectedBehaviors.length > 0) ? diningBehavior.selectedBehaviors : undefined,
      diningGroup: undefined,
      menuGroup: undefined
    };

    const chartData = getChartData(dashboardData, 'hourly', filters);
    
    if (!chartData || !Array.isArray(chartData)) {
      return []; 
    }

    const filteredData = chartData.filter(
      (hourData: HourlyData) => extractNumber(hourData.avg_hourly_sales) > 0 || parseInt(hourData.order_count) > 0
    );
    
    const sortedData = [...filteredData].sort((a, b) => a.hour - b.hour);

    return sortedData;
  };

  const closeMonthlyTrend = () => {
    setShowMonthlyTrend(false);
    setAllLegendsSelected(true);
  };

  const getMonthlyTrendOption = () => {
    if (!monthlyTrendData || !Array.isArray(monthlyTrendData) || monthlyTrendData.length === 0) {
      return {};
    }

    const months = monthlyTrendData.map((item: MonthlyHourlyData) => formatMonthForDisplay(item.year_month));
    
    const series: any[] = [];
    const legendData: string[] = [];
    const allValues: number[] = [];

    
    const allHours = new Set<number>();
    monthlyTrendData.forEach((monthData: MonthlyHourlyData) => {
      if (monthData.hours) {
        Object.keys(monthData.hours).forEach(hour => {
          allHours.add(parseInt(hour));
        });
      }
    });
    
    const sortedHours = Array.from(allHours).sort((a, b) => a - b);

    sortedHours.forEach((hour) => {
      const hourLabel = formatHour(hour);
      const hourData = monthlyTrendData.map((monthData: MonthlyHourlyData) => {
        const hourInfo = monthData.hours ? monthData.hours[hour] : null;
        const value = hourInfo ? extractNumber(hourInfo.avg_daily_sales) : 0;
        if (value > 0) allValues.push(value);
        return value;
      });

      series.push({
        name: hourLabel,
        type: 'line',
        data: hourData,
        lineStyle: {
          width: isLaptop ? 2 : 2.5,
          color: hourColors[hour] || '#474646'
        },
        itemStyle: {
          color: hourColors[hour] || '#474646'
        },
        symbol: 'circle',
        symbolSize: isLaptop ? 4 : 5,
        smooth: false
      });

      legendData.push(hourLabel);
    });

    const yAxisRange = calculateYAxisRange(allValues);
    
    return {
      grid: {
        left: '4%',
        right: '4%',
        top: (window.innerWidth <= 480) ? '33%' : (isLaptop ? '20%' : '22%'),
        bottom: (window.innerWidth <= 480) ? '0%' : '2%',
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333',
          fontSize: isLaptop ? 10 : 11
        },
        confine: true, 
        formatter: function(params: any) {
          const monthStr = params[0].axisValue;
          let html = `<div style="padding: 3px;">
            <div style="font-weight: bold; margin-bottom: ${isLaptop ? '5px' : '6px'}; font-size: ${isLaptop ? '11px' : '12px'};">${monthStr}</div>`;
          
          const validParams = params.filter((param: any) => param.value > 0);
          
          validParams.forEach((param: any) => {
            html += `<div style="display: flex; justify-content: space-between; margin-bottom: 3px;">
              <span style="margin-right: 10px; display: inline-flex; align-items: center;">
                <span style="display: inline-block; width: 8px; height: 8px; background-color: ${param.color}; margin-right: 4px; border-radius: 50%;"></span>
                <strong>${param.seriesName}:</strong>
              </span>
              <span style="font-weight: bold; font-size: ${isLaptop ? '10px' : '11px'};">$${param.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>`;
          });
          
          html += '</div>';
          return html;
        }
      },
      legend: {
        data: legendData,
        top: isLaptop ? '8%' : '10%',
        left: 'center',
        width: '90%',
        textStyle: {
          fontSize: (window.innerWidth <= 480) ? 10 : (isLaptop ? 10 : 11),
          fontWeight: 'bold'
        },
        itemGap: isLaptop ? 6 : 8,
        itemWidth: isLaptop ? 10 : 12,
        itemHeight: isLaptop ? 6 : 8,
        type: (window.innerWidth <= 480) ? 'plain' : 'scroll',
        orient: 'horizontal',
        selector: false,
        itemStyle: {
          borderWidth: 0
        },
        lineStyle: {
          width: isLaptop ? 2.5 : 3
        }
      },
      graphic: [
        {
          type: 'text',
          left: 'center',
          top: '1%',
          style: {
            text: t('hourlyChart.chartHint'),
            fontSize: isLaptop ? 9 : 10,
            fontWeight: 'normal',
            fill: '#666',
            fontStyle: 'italic'
          }
        }
      ],
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: isLaptop ? 9 : 10,
          fontWeight: 'bold',
          rotate: months.length > 8 ? 45 : 0,
          margin: isLaptop ? 5 : 6
        },
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: {
        type: 'value',
        name: t('hourlyChart.yAxisHourlyUSD'),
        min: yAxisRange.min,
        scale: true,
        nameTextStyle: {
          fontSize: isLaptop ? 10 : 11,
          fontWeight: 'bold',
          padding: [0, 0, 0, 20]
        },
        axisLabel: {
          fontSize: isLaptop ? 9 : 10,
          
          formatter: '${value}'
        },
        splitLine: {
          lineStyle: {
            opacity: 0.3
          }
        }
      },
      series: series
    };
  };

  const getOption = () => {
    const hourlyData = getFilteredHourlyData();
    
    if (hourlyData.length === 0) {
      return {};
    }
    
    const hourLabels = hourlyData.map(hour => formatHour(hour.hour));
    const salesData = hourlyData.map(hour => extractNumber(hour.avg_hourly_sales));
    
    const validSalesData = salesData.filter(sale => sale > 0);
    const maxSales = Math.max(...validSalesData);
    const minSales = Math.min(...validSalesData);
    
    const maxIndex = salesData.indexOf(maxSales);
    const minIndex = salesData.indexOf(minSales);
    
    const formattedSalesData = salesData.map((value, index) => {
      if (index === maxIndex) {
        return {
          value,
          itemStyle: {
            color: colorPalette.highlightColor,
            borderRadius: [6, 6, 0, 0]
          },
          label: {
            show: true,
            position: 'top',
            formatter: `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
            fontSize: isIPhone ? 10 : (isIPad ? 11 : (isLaptop ? 12 : 14)),
            fontWeight: 'bold',
            color: colorPalette.highlightColor
          }
        };
      } else if (index === minIndex && value > 0) {
        return {
          value,
          itemStyle: {
            color: colorPalette.lowlightColor,
            borderRadius: [6, 6, 0, 0]
          },
          label: {
            show: true,
            position: 'top',
            formatter: `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
            fontSize: isIPhone ? 10 : (isIPad ? 11 : (isLaptop ? 12 : 14)),
            fontWeight: 'bold',
            color: colorPalette.lowlightColor
          }
        };
      } else {
        return {
          value,
          itemStyle: {
            color: colorPalette.primaryColor,
            borderRadius: [6, 6, 0, 0]
          }
        };
      }
    });
    
    return {
      tooltip: {
        trigger: 'axis',
        confine: true, 
        formatter: function(params: any) {
          const hourIndex = params[0].dataIndex;
          const hourDataItem = hourlyData[hourIndex];
          const totalSales = extractNumber(hourDataItem.net_sales);
          const orderCount = hourDataItem.order_count;
          const avgSales = extractNumber(hourDataItem.avg_hourly_sales);
          
          return `<div style="padding: 3px; border-left: 4px solid ${colorPalette.primaryColor};">
            <div style="font-weight: bold; margin-bottom: ${isLaptop ? '6px' : '8px'}; font-size: ${isLaptop ? '13px' : '14px'};">${formatHour(hourDataItem.hour)}</div>
            <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
              <span style="margin-right: 15px;">${t('hourlyChart.avgHourlySales')}:</span>
              <span style="font-weight: bold;">$${avgSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: ${isLaptop ? '4px' : '5px'};">
              <span style="margin-right: 15px;">${t('hourlyChart.totalSales')}:</span>
              <span style="font-weight: bold;">$${totalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div style="display: flex; justify-content: space-between;">
              <span style="margin-right: 15px;">${t('hourlyChart.orders')}:</span>
              <span style="font-weight: bold;">${orderCount}</span>
            </div>
          </div>`;
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: isLaptop ? '45px' : '50px', 
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: hourLabels,
        axisLabel: {
          fontWeight: 'bold',
          fontSize: isIPhone ? 9 : (isIPad ? 10 : (isLaptop ? 11 : 12)),
          color: colorPalette.textColor,
          interval: 0,
          rotate: isIPhone ? 45 : (hourLabels.length > 10 ? 45 : 0)
        },
        axisTick: {
          alignWithLabel: true
        }
      },
      yAxis: {
        type: 'value',
        name: t('hourlyChart.yAxisHourlyUSD'),
        nameTextStyle: {
          fontSize: isIPhone ? 10 : (isIPad ? 11 : (isLaptop ? 11 : 12)),
          color: colorPalette.textColor,
          padding: [0, 0, 0, 10]
        },
        axisLabel: {
          
          formatter: '${value}',
          fontWeight: 'bold',
          fontSize: isIPhone ? 10 : (isIPad ? 11 : (isLaptop ? 11 : 12)),
          color: colorPalette.textColor
        }
      },
      series: [
        {
          name: t('hourlyChart.avgHourlySales'),
          type: 'bar',
          barWidth: isIPhone ? '40%' : (isIPad ? '42%' : (isLaptop ? '45%' : '50%')),
          data: formattedSalesData
        }
      ]
    };
  };

  if (loading || isFiltersApplying) {
    return (
      <div className="hourly-sales-chart">
        <div className="hourly-title-band">
          <h2 style={{
            fontSize: isLaptop ? '16px' : '18px',
            padding: isLaptop ? '8px 20px' : '10px 25px'
          }}>
            {t('hourlyChart.title')}
          </h2>
        </div>
        <div className="hourly-chart-container chart-container">
          <div className="hourly-loading">
            <div className="hourly-loading-spinner"></div>
            <div className="hourly-loading-text">
              {isFiltersApplying ? t('hourlyChart.filterApplying') : t('hourlyChart.loading')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hourly-sales-chart">
        <div className="hourly-title-band">
          <h2 style={{
            fontSize: isLaptop ? '16px' : '18px',
            padding: isLaptop ? '8px 20px' : '10px 25px'
          }}>
            {t('hourlyChart.title')}
          </h2>
        </div>
        <div className="hourly-chart-container">
          <div className="hourly-error">
            <div className="hourly-error-icon">⚠️</div>
            <div className="hourly-error-text">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="hourly-sales-chart">
        {isFiltersApplying && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <span>{t('hourlyChart.filterApplying')}</span>
          </div>
        )}
        
        <div className="hourly-title-band">
          <h2 style={{
            fontSize: isLaptop ? '16px' : '18px',
            padding: isLaptop ? '8px 20px' : '10px 25px'
          }}>
            {t('hourlyChart.title')}
          </h2>
        </div>
        <div className="hourly-chart-container">
          <div className="hourly-navigation">
            <button 
              className="hourly-monthly-trend-button" 
              onClick={handleMonthlyTrendClick}
              disabled={isFiltersApplying}
              style={{
                fontSize: isLaptop ? '11px' : '12px',
                padding: isLaptop ? '5px 10px' : '6px 12px'
              }}
            >
              {t('hourlyChart.viewMonthlyTrend')}
            </button>
          </div>
          <ReactECharts 
            option={getOption()} 
            style={{ height: isIPhone ? '320px' : (isIPad ? '350px' : (isLaptop ? '380px' : '460px')), width: '100%' }}
            className="hourly-echarts"
            opts={{ renderer: 'canvas' }}
          />
        </div>
      </div>

      {showMonthlyTrend && (
        <div className="hourly-trend-overlay" onClick={closeMonthlyTrend}>
          <div 
            className="hourly-trend-modal" 
            onClick={(e) => e.stopPropagation()}
            style={isPhoneScreen ? {
              position: 'fixed',
              left: '2.5%',
              top: `${modalPosition.y}px`,
              width: '95%',
              height: '500px',
              margin: '0',
              transform: 'none'
            } : {
              position: 'fixed',
              left: '50%',
              top: `${modalPosition.y}px`,
              transform: 'translateX(-50%)'
            }}
          >
            <div className="hourly-trend-modal-header">
              <h3 className="hourly-trend-modal-title">{t('hourlyChart.modalTitle')}</h3>
              <div className="hourly-trend-modal-subtitle">{t('hourlyChart.modalSubtitle')}</div>
              <button className="hourly-trend-modal-close" onClick={closeMonthlyTrend}>
                ✕
              </button>
            </div>
            <div className="hourly-trend-modal-content">
              {trendLoading ? (
                <div className="hourly-trend-loading">
                  <div className="hourly-trend-loader"></div>
                  <div>{t('hourlyChart.loadingMonthlyTrend')}</div>
                </div>
              ) : trendError ? (
                <div className="hourly-trend-error">
                  <div className="hourly-trend-error-icon">⚠️</div>
                  <div>{trendError}</div>
                </div>
              ) : monthlyTrendData && Array.isArray(monthlyTrendData) && monthlyTrendData.length > 0 ? (
                <div className="hourly-trend-chart-wrapper">
                  <div className="hourly-legend-control-top">
                    <span className="hourly-legend-hint">{t('hourlyChart.legendHint')}</span>
                    <button 
                      className="hourly-toggle-all-button"
                      onClick={handleToggleAllLegends}
                      type="button"
                    >
                      {allLegendsSelected ? t('hourlyChart.deselectAll') : t('hourlyChart.selectAll')}
                    </button>
                  </div>
                  <ReactECharts 
                    ref={trendChartRef}
                    option={getMonthlyTrendOption()} 
                    style={{height: '100%', width: '100%'}}
                    className="hourly-trend-chart"
                    opts={{renderer: 'canvas'}}
                  />
                </div>
              ) : (
                <div className="hourly-trend-empty">
                  <div>{t('hourlyChart.noTrendData')}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HourlySalesChart;